/**
 * Customer route guards.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useEffect } from 'react'
import type { CustomerRouteMode } from '../routes'

const ELEVATED_ROLES = new Set(['admin', 'super-admin', 'superadmin', 'site-manager', 'sitemanager'])

export const hasElevatedRole = (roles: string[] | undefined) =>
  Array.isArray(roles) &&
  roles.some((role) => {
    const normalized = String(role || '').trim().toLowerCase()
    return ELEVATED_ROLES.has(normalized)
  })

type ShouldRedirectToAdminInput = {
  accountRoles: string[] | undefined
}

export const shouldRedirectToAdmin = ({ accountRoles }: ShouldRedirectToAdminInput) => {
  if (hasElevatedRole(accountRoles)) return true
  return false
}

type Params = {
  initializing: boolean
  accountRoles: string[] | undefined
  authenticated: boolean
  mode: CustomerRouteMode
  nextPath: string
  nextPathReady: boolean
  onNavigate: (targetPath: string) => void
}

const resolveAdminLocation = (): string => {
  if (typeof window === 'undefined') return '/admin'
  const { protocol, hostname, port } = window.location
  if (hostname.startsWith('shop.')) {
    const adminHostname = `admin.${hostname.slice('shop.'.length)}`
    const host = port ? `${adminHostname}:${port}` : adminHostname
    return `${protocol}//${host}/`
  }
  return '/admin'
}

/**
 * Use customer route guards.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useCustomerRouteGuards = ({
  initializing,
  accountRoles,
  authenticated,
  mode,
  nextPath,
  nextPathReady,
  onNavigate,
}: Params) => {
  useEffect(() => {
    if (initializing) return
    if (!shouldRedirectToAdmin({ accountRoles })) return
    if (typeof window !== 'undefined') {
      window.location.assign(resolveAdminLocation())
      return
    }
    onNavigate('/admin')
  }, [accountRoles, initializing, onNavigate])

  useEffect(() => {
    if (!authenticated || initializing) return
    if (!nextPathReady) return
    if (mode === 'login' || mode === 'signup' || mode === 'activate' || mode === 'forgot-password' || mode === 'reset-password') {
      onNavigate(mode === 'signup' ? '/dashboard' : nextPath)
    }
  }, [authenticated, initializing, mode, nextPath, nextPathReady, onNavigate])
}
